<script setup lang="ts">
    import { calculateCoachingFormQuestionPoints } from './coachingForm.utils';
    import { __ } from '@/js/stores/expressions';
    import IconButton from '../../IconButton.vue';
    import RadioOption from '../../RadioOption.vue';
    import { computed, ref } from 'vue';
    import TextareaInput from '../../TextareaInput.vue';
    import MarkdownTextarea from '../../MarkdownTextarea.vue';
    import { CoachingFormAnswerType } from '@/js/services/api/coachingForm/types';
    import EchoPopover from '../../EchoPopover.vue';
    import { ProtocolFormStateQuestion } from './coachingForm.types';
    import ProtocolComments from '../protocol/detail/ProtocolComments.vue';
    import { AstroidIcon, InfoIcon, MessageSquareIcon } from '@lucide/vue';
    import ParsedMarkdown from '../../ParsedMarkdown.vue';

    const question = defineModel<ProtocolFormStateQuestion>('question', { required: true });

    const props = defineProps<{
        protocolId?: string;
        preview?: boolean;
    }>();

    const questionPoints = computed(() => calculateCoachingFormQuestionPoints(question.value));

    const commentsCount = ref(0);

    // Covers both the free-text comment note and any threaded protocol comments.
    const hasComment = computed(() => !!question.value.comment?.trim() || commentsCount.value > 0);

    const showComment = ref(!!question.value.comment?.trim() || props.preview);

    const toggleComment = () => {
        showComment.value = !showComment.value;
    };
</script>
<template>
    <div class="question-row-grid">
        <template v-if="question.answerType === CoachingFormAnswerType.Text">
            <div class="col-span-3 px-5 py-3.5">
                <div class="flex justify-between items-center mb-2 gap-2 w-[30%] pr-7">
                    <p class="text-body">
                        {{ question.title }}
                    </p>
                    <div class="flex gap-2">
                        <EchoPopover v-if="question.description" position="top-end">
                            <template #button="{ anchor, togglePopover, buttonAttrs }">
                                <IconButton
                                    :ref="anchor"
                                    :aria-label="__('description')"
                                    :tooltip="__('description')"
                                    size="size-6"
                                    area-size="size-8"
                                    v-bind="buttonAttrs"
                                    class="text-gray-medium hover:bg-gray-lightest hover:text-gray-dark"
                                    @click="togglePopover"
                                >
                                    <InfoIcon class="size-5" />
                                </IconButton>
                            </template>
                            <template #popover>
                                <div class="w-96 max-h-72 overflow-y-auto">
                                    <ParsedMarkdown
                                        :markdown="question.description"
                                        :breaks="true"
                                    />
                                </div>
                            </template>
                        </EchoPopover>
                        <EchoPopover v-if="question.prompt" position="top-end">
                            <template #button="{ anchor, togglePopover, buttonAttrs }">
                                <IconButton
                                    :ref="anchor"
                                    :aria-label="__('question-prompt')"
                                    :tooltip="__('question-prompt')"
                                    size="size-6"
                                    area-size="size-8"
                                    v-bind="buttonAttrs"
                                    class="text-gray-medium hover:bg-gray-lightest hover:text-gray-dark"
                                    @click="togglePopover"
                                >
                                    <AstroidIcon class="size-5" />
                                </IconButton>
                            </template>
                            <template #popover>
                                <div class="w-96 max-h-72 overflow-y-auto">
                                    <ParsedMarkdown :markdown="question.prompt" :breaks="true" />
                                </div>
                            </template>
                        </EchoPopover>
                    </div>
                </div>
                <div
                    v-if="question.answerType === CoachingFormAnswerType.Text"
                    class="flex items-center gap-2 w-full"
                >
                    <p v-if="preview" class="text-body">
                        {{ question.textValue }}
                    </p>
                    <TextareaInput
                        v-if="!preview"
                        v-model="question.textValue"
                        :placeholder="__('coaching-form-text-answer-placeholder')"
                        class="w-1/2"
                        :rows="2"
                    />
                </div>
            </div>
        </template>
        <template v-else>
            <div class="pl-5 pr-4.5 py-3.5 h-full flex items-center">
                <div class="flex justify-between items-center w-full">
                    <p class="text-body">
                        {{ question.title }}
                    </p>
                    <div class="flex gap-1 shrink-0">
                        <IconButton
                            v-if="question.useComments"
                            :aria-label="__('comment')"
                            size="size-6"
                            area-size="size-8"
                            class="hover:bg-gray-lightest hover:text-gray-dark"
                            :class="
                                showComment ? 'bg-gray-lightest text-gray-dark' : 'text-gray-medium'
                            "
                            @click="toggleComment"
                        >
                            <MessageSquareIcon class="size-5" />
                            <span
                                v-if="hasComment"
                                class="absolute -top-0.5 -right-0.5 size-1.75 rounded-full bg-primary-beet"
                            />
                        </IconButton>
                        <EchoPopover v-if="question.description">
                            <template #button="{ anchor, togglePopover, buttonAttrs }">
                                <IconButton
                                    :ref="anchor"
                                    :aria-label="__('description')"
                                    :tooltip="__('description')"
                                    size="size-6"
                                    area-size="size-8"
                                    v-bind="buttonAttrs"
                                    class="text-gray-medium hover:bg-gray-lightest hover:text-gray-dark"
                                    @click="togglePopover"
                                >
                                    <InfoIcon class="size-5" />
                                </IconButton>
                            </template>
                            <template #popover>
                                <div class="w-96 max-h-72 overflow-y-auto">
                                    <ParsedMarkdown
                                        :markdown="question.description"
                                        :breaks="true"
                                    />
                                </div>
                            </template>
                        </EchoPopover>
                        <EchoPopover v-if="question.prompt">
                            <template #button="{ anchor, togglePopover, buttonAttrs }">
                                <IconButton
                                    :ref="anchor"
                                    :aria-label="__('question-prompt')"
                                    :tooltip="__('question-prompt')"
                                    size="size-6"
                                    area-size="size-8"
                                    v-bind="buttonAttrs"
                                    class="text-gray-medium hover:bg-gray-lightest hover:text-gray-dark"
                                    @click="togglePopover"
                                >
                                    <AstroidIcon class="size-5" />
                                </IconButton>
                            </template>
                            <template #popover>
                                <div class="w-96 max-h-72 overflow-y-auto">
                                    <ParsedMarkdown :markdown="question.prompt" :breaks="true" />
                                </div>
                            </template>
                        </EchoPopover>
                    </div>
                </div>
            </div>
            <div class="min-w-0 pr-4.5 py-3.5 h-full flex items-center">
                <div class="flex flex-wrap gap-x-4 gap-y-1 border-l border-gray-light pl-4.5">
                    <div
                        v-for="answer in question.answers"
                        :key="answer.id"
                        class="flex items-center justify-start gap-2"
                    >
                        <RadioOption
                            :id="'answer-' + answer.id + '-' + protocolId"
                            v-model="question.selectedOptionId"
                            :name="'question-' + question.id + '-' + protocolId"
                            :disabled="preview"
                            :label="answer.label"
                            :value="answer.id"
                        />
                    </div>
                </div>
            </div>
            <div class="pr-5 py-3.5 h-full flex items-center">
                <div class="flex min-w-20 border-l border-gray-light pl-4.5">
                    <p v-if="questionPoints.isNotApplicable" class="text-body">—</p>
                    <p v-else-if="questionPoints.maxPoints > 0" class="text-body">
                        {{ `${questionPoints.points} / ${questionPoints.maxPoints}` }}
                    </p>
                </div>
            </div>
        </template>
    </div>
    <template v-if="question.useComments && showComment">
        <div class="question-row-grid">
            <div class="col-span-3 px-5 py-3">
                <div class="mb-3">
                    <h2 class="text-body-bold mb-1">
                        {{ __('comment') }}
                    </h2>
                    <ParsedMarkdown
                        v-if="preview"
                        :markdown="question.comment || __('no-comment')"
                        :breaks="true"
                    />
                    <MarkdownTextarea
                        v-if="!preview"
                        v-model="question.comment"
                        :placeholder="__('coaching-form-add-comment-placeholder')"
                        class="comment-editor"
                        :rows="3"
                    />
                </div>

                <ProtocolComments
                    v-if="protocolId"
                    :key="protocolId"
                    :protocol-id="protocolId"
                    :protocol-question-answer-id="question.protocolQuestionAnswerId"
                    :disabled="!preview"
                    @update:comments-count="commentsCount = $event"
                />
            </div>
        </div>
    </template>
</template>

<style scoped>
    .comment-editor {
        width: 50%;
    }

    .comment-editor :deep(textarea) {
        width: 100%;
    }
</style>
